import { useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Upload } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

const IMG = /\.(jpe?g|png|webp)$/i;

// Downscale in the browser so campaign photos (often 5-20 MB) upload fast and light.
const shrink = (file, max = 2000) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const r = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    if (r === 1 && file.size < 1.5e6) return resolve(file);
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * r);
    c.height = Math.round(img.naturalHeight * r);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const png = /png$/i.test(file.name);
    c.toBlob((b) => resolve(b || file), png ? "image/png" : "image/jpeg", 0.9);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const LocalPhotos = ({ onDone }) => {
  const call = useApi();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [matches, setMatches] = useState(null);
  const [progress, setProgress] = useState(null);

  const pick = async (e) => {
    const list = [...e.target.files].filter((f) => IMG.test(f.name));
    if (!list.length) { toast.error("La carpeta no tiene fotos JPG/PNG/WEBP"); return; }
    setFiles(list);
    setMatches(null);
    try {
      const { data } = await call("post", "/admin/photos/match", { names: list.map((f) => f.name) });
      setMatches(data);
      const n = data.filter((m) => m.status === "match").length;
      toast[n ? "success" : "info"](n ? `${n} de ${list.length} fotos corresponden a artículos de la web` : "Ninguna foto coincide con tus referencias");
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  const upload = async () => {
    const todo = matches.filter((m) => m.status === "match");
    setProgress({ done: 0, total: todo.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < todo.length; i++) {
      const m = todo[i];
      const file = files.find((f) => f.name === m.name);
      try {
        const blob = await shrink(file);
        const fd = new FormData();
        fd.append("file", blob, m.name);
        fd.append("product_id", m.product_id);
        fd.append("color", m.color || "");
        fd.append("name", m.name);
        await call("post", "/admin/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { failed++; }
      setProgress({ done: i + 1, total: todo.length, failed });
    }
    toast.success(`Subidas ${todo.length - failed} fotos${failed ? ` · ${failed} fallaron` : ""}`);
    setMatches(null);
    setFiles([]);
    onDone?.();
  };

  const count = (s) => matches?.filter((m) => m.status === s).length || 0;
  const products = new Set(matches?.filter((m) => m.status === "match").map((m) => m.product_id)).size;
  return (
    <div className="bg-white border p-6 max-w-3xl space-y-4" data-testid="admin-local-photos">
      <p className="filter-title flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Fotos desde tu ordenador (carpeta completa)</p>
      <p className="text-sm text-slate-600">Elige la carpeta con <b>todas</b> las fotos de la campaña (p. ej. la descarga de 4F). El navegador solo lee los nombres: la web comprueba cuáles corresponden a tus artículos por la referencia y <b>sube únicamente esas</b>, reducidas a tamaño web. Las demás no se suben. Mismas reglas de nombre que en Drive.</p>
      <input ref={inputRef} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={pick} data-testid="local-photos-input" />
      <div className="flex flex-wrap gap-3">
        <button className="btn-outline !h-11" onClick={() => inputRef.current?.click()} disabled={!!progress && progress.done < progress.total} data-testid="local-photos-pick"><FolderOpen className="w-4 h-4 mr-2" /> Elegir carpeta</button>
        {matches && count("match") > 0 && !progress && (
          <button className="btn-primary !h-11" onClick={upload} data-testid="local-photos-upload"><Upload className="w-4 h-4 mr-2" /> Subir {count("match")} fotos de {products} artículos</button>
        )}
      </div>
      {progress && <p className="text-sm" data-testid="local-photos-progress">Subiendo {progress.done}/{progress.total}{progress.failed ? ` · ${progress.failed} fallidas` : ""}…</p>}
      {matches && (
        <div className="space-y-3 text-sm" data-testid="local-photos-result">
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="bg-emerald-50 p-3"><p className="text-xs text-slate-500">Coinciden</p><p className="font-bold text-lg" data-testid="local-photos-match">{count("match")}</p></div>
            <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Ya subidas</p><p className="font-bold text-lg">{count("already")}</p></div>
            <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Artículo lleno (7)</p><p className="font-bold text-lg">{count("full")}</p></div>
            <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Sin artículo</p><p className="font-bold text-lg" data-testid="local-photos-unmatched">{count("unmatched")}</p></div>
          </div>
          {count("match") > 0 && (
            <details><summary className="cursor-pointer font-semibold">Ver las que se van a subir</summary>
              <ul className="mt-2 max-h-60 overflow-y-auto divide-y font-mono text-xs">{matches.filter((m) => m.status === "match").map((m) => <li key={m.name} className="py-1 flex gap-3"><span className="truncate">{m.name}</span><span className="ml-auto text-slate-500 truncate font-sans">{m.product}{m.color ? ` · ${m.color}` : ""}</span></li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
